import { Badge, Button, Calendar, CalendarProps, Flex, Space, Tag } from "antd"
import Title from "antd/es/typography/Title"
import { useTranslation } from "react-i18next"
import { usePreassignations } from "./queries"
import { useEffect, useRef, useState } from "react"
import Loader from "components/loader"
import styles from "../styles.module.scss"
import { IPreassignation } from "./types"
import OverviewCardWithDate from "components/overview-card-with-date"

import { ReactComponent as InfoIcon } from "assets/icons/info.svg"
import { ReactComponent as AddIcon } from "assets/icons/add.svg"
import { ReactComponent as DeleteIcon } from "assets/icons/delete.svg"
import { usePopup } from "utils/use-popup"
import EventDetailsModal from "../event-details-modal"
import { IEventDetail } from "pages/events/types"
import DeletePreassignationModal from "./delete"
import { useNavigate } from "react-router-dom"
import PageHeader from "modules/layout/dashboard/main/page-header"
import dayjs from "dayjs"
import useWindowDimensions from "utils/use-window-dimension"

const statusTagColors = (status: IPreassignation["status"]) => {
  switch (status) {
    case "draft":
    case "approved":
      return "var(--color-success)"
    case "refused":
      return "var(--color-accent2)"
    default:
      return "var(--color-error)"
  }
}

const Preassignations = () => {
  const { t } = useTranslation()
  const { width } = useWindowDimensions()
  const navigate = useNavigate()
  const [calendarValue, setCalendarValue] =
    useState<CalendarProps<any>["value"]>(dayjs)
  const [calendarMode, setCalendarMode] =
    useState<CalendarProps<any>["mode"]>("month")
  const listRef = useRef(null)
  let shouldScroll = useRef(false)
  const preassignations = usePreassignations()

  const eventDetailsModal = usePopup<IEventDetail>()
  const deleteModal = usePopup<string>()

  const CellRender: CalendarProps<any>["cellRender"] = (current, info) => {
    let events: IPreassignation[] = []
    if (info.type === "date") {
      events =
        preassignations.data?.filter(
          (preassignation) =>
            preassignation.event.start_date === current.format("YYYY-MM-DD")
        ) ?? []
    }
    if (info.type === "month") {
      events =
        preassignations.data?.filter(
          (preassignation) =>
            parseInt(preassignation.event.start_date.split("-")[1]) ===
            current.month() + 1
        ) ?? []
    }

    return (
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginTop: "5px",
        }}
      >
        <Badge
          showZero
          count={events?.length}
          style={{
            backgroundColor:
              events?.length !== 0 ? "var(--color-primary)" : "transparent",
          }}
        />
      </div>
    )
  }

  const handleSelect: CalendarProps<any>["onSelect"] = (date, selectInfo) => {
    shouldScroll.current = true
  }

  const handlePanelChange: CalendarProps<any>["onPanelChange"] = (
    date,
    mode
  ) => {
    setCalendarMode(mode)
  }

  useEffect(() => {
    if (shouldScroll.current && listRef.current) {
      ;(listRef.current as any)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      })
      shouldScroll.current = false
    }
  })

  return (
    <>
      <Flex justify="space-between">
        <PageHeader
          title={
            <Title level={4}>{t("assignations.preassignation_other")}</Title>
          }
          hasBackButton={false}
          rightContent={
            <Button
              style={{
                display: "flex",
                alignItems: "center",
              }}
              size={width! > 520 ? "large" : "middle"}
              type="primary"
              onClick={() => navigate("create")}
            >
              <AddIcon style={width! > 520 ? { marginRight: 10 } : {}} />
              {width! > 520 &&
                t("global.new", {
                  entity: t("assignations.preassignation_one"),
                  context: "female",
                  count: 1,
                })}
            </Button>
          }
        />
      </Flex>
      <div className={styles.calendarContainer}>
        {preassignations.isLoading && (
          <div className={styles.calendarLoader}>
            <Loader />
          </div>
        )}
        <Calendar
          fullscreen={false}
          value={calendarValue}
          onChange={setCalendarValue}
          cellRender={CellRender}
          onPanelChange={handlePanelChange}
          mode={calendarMode}
          onSelect={handleSelect}
        />
      </div>
      <Space direction="vertical" ref={listRef}>
        {preassignations.data
          ?.filter((el) =>
            calendarValue
              ? calendarMode === "month"
                ? el.event.start_date === calendarValue.format("YYYY-MM-DD")
                : parseInt(el.event.start_date.split("-")[1]) ===
                  calendarValue.month() + 1
              : false
          )
          .sort(
            (a, b) =>
              new Date(a.event.start_date).getTime() -
              new Date(b.event.start_date).getTime()
          )
          .map(({ id, status, displayed_status, event }) => (
            <OverviewCardWithDate
              key={id}
              date={event.start_date}
              actions={[
                <Tag
                  style={{
                    marginRight: 0,
                  }}
                  color={statusTagColors(status)}
                >
                  {displayed_status}
                </Tag>,
                <Button
                  style={{
                    width: "100%",
                  }}
                  icon={<InfoIcon height={20} />}
                  size="small"
                  type="text"
                  onClick={() => eventDetailsModal.open(event)}
                />,
                <Button
                  style={{
                    width: "100%",
                  }}
                  icon={<DeleteIcon height={17} />}
                  size="small"
                  type="text"
                  disabled={status !== "draft"}
                  onClick={() => deleteModal.open(id)}
                />,
              ]}
              Content={
                <>
                  <p>{event.name}</p>
                  <p>
                    {event.start_hour}
                    {" - "}
                    {event.end_hour}
                  </p>
                  <p>{event.place?.name}</p>
                </>
              }
            />
          ))}
      </Space>
      <EventDetailsModal
        {...eventDetailsModal}
        open={eventDetailsModal.isOpen}
      />
      <DeletePreassignationModal {...deleteModal} open={deleteModal.isOpen} />
    </>
  )
}

export default Preassignations
